import { useEffect, useRef, useState } from "react";
import { Camera, X, RefreshCw, Check } from "lucide-react";

/**
 * Live camera modal — opens getUserMedia stream, big snap button.
 * onCapture(base64WithoutPrefix) is called when the user accepts the shot.
 */
export default function LiveCameraCapture({ onCapture, onClose }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [error, setError] = useState("");
  const [snapped, setSnapped] = useState("");
  const [starting, setStarting] = useState(true);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: "environment" }, width: { ideal: 1280 }, height: { ideal: 1280 } },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
        }
        setStarting(false);
      } catch (e) {
        setError(e?.message || "Camera blocked. Allow camera permission and reload.");
        setStarting(false);
      }
    })();

    return () => {
      cancelled = true;
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    };
  }, []);

  const snap = () => {
    const v = videoRef.current;
    const c = canvasRef.current;
    if (!v || !c) return;
    const w = v.videoWidth || 720;
    const h = v.videoHeight || 720;
    c.width = w; c.height = h;
    const ctx = c.getContext("2d");
    ctx.drawImage(v, 0, 0, w, h);
    const dataUrl = c.toDataURL("image/jpeg", 0.9);
    setSnapped(dataUrl);
  };

  const accept = () => {
    if (!snapped) return;
    const b64 = snapped.split(",")[1];
    onCapture?.(b64, snapped);
    onClose?.();
  };

  return (
    <div data-testid="live-camera-modal" className="fixed inset-0 z-[100] bg-[#0B0F0C]/95 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="card-x card-magenta bg-[#FAF6E8] p-4 max-w-md w-full">
        <div className="flex items-center gap-2 mb-3">
          <Camera size={16} strokeWidth={2.4} />
          <span className="font-display text-2xl font-extrabold">Snap a nug</span>
          <button data-testid="live-camera-close" onClick={onClose} className="ml-auto w-9 h-9 rounded-full border-2 border-[#0B0F0C] flex items-center justify-center hover:bg-[#FF1A8C] hover:text-white">
            <X size={14} strokeWidth={2.4} />
          </button>
        </div>

        <div className="relative aspect-square rounded-2xl overflow-hidden border-2 border-[#0B0F0C] bg-black">
          {snapped ? (
            <img src={snapped} alt="captured" className="w-full h-full object-cover" />
          ) : (
            <video
              ref={videoRef}
              playsInline
              muted
              className="w-full h-full object-cover"
            />
          )}
          {starting && !error && (
            <div className="absolute inset-0 flex items-center justify-center text-[#FAF6E8] text-xs font-mono-x font-bold uppercase tracking-widest">
              Starting camera…
            </div>
          )}
          {error && (
            <div className="absolute inset-0 flex items-center justify-center p-5 text-[#FAF6E8] text-sm text-center font-medium">
              {error}
            </div>
          )}
          {/* viewfinder corners */}
          {!snapped && !error && (
            <>
              <span className="absolute top-3 left-3 w-6 h-6 border-t-[3px] border-l-[3px] border-[#15FF73]" />
              <span className="absolute top-3 right-3 w-6 h-6 border-t-[3px] border-r-[3px] border-[#15FF73]" />
              <span className="absolute bottom-3 left-3 w-6 h-6 border-b-[3px] border-l-[3px] border-[#15FF73]" />
              <span className="absolute bottom-3 right-3 w-6 h-6 border-b-[3px] border-r-[3px] border-[#15FF73]" />
            </>
          )}
        </div>

        <canvas ref={canvasRef} className="hidden" />

        {snapped ? (
          <div className="grid grid-cols-2 gap-2 mt-4">
            <button data-testid="live-camera-retake" onClick={() => setSnapped("")} className="btn-x btn-ghost justify-center">
              <RefreshCw size={14} strokeWidth={2.4} /> Retake
            </button>
            <button data-testid="live-camera-accept" onClick={accept} className="btn-x btn-primary justify-center">
              <Check size={14} strokeWidth={2.6} /> Use this
            </button>
          </div>
        ) : (
          <button
            data-testid="live-camera-snap"
            onClick={snap}
            disabled={starting || !!error}
            className="mt-4 w-16 h-16 mx-auto rounded-full border-[5px] border-[#0B0F0C] bg-[#FF1A8C] hover:bg-[#15FF73] active:scale-90 transition-all flex items-center justify-center disabled:opacity-40"
            aria-label="Capture photo"
          >
            <span className="w-10 h-10 rounded-full bg-[#FAF6E8] border-2 border-[#0B0F0C]" />
          </button>
        )}
      </div>
    </div>
  );
}
